import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Mail, Phone, ArrowRight, ArrowUp } from "lucide-react";
import Logo from "../Accets/logo.png";
import { RevealSection, staggerContainer, staggerItem, MagneticButton } from "./motion/ScrollFX";

// lucide-react no longer ships brand/logo icons (Facebook, Instagram,
// LinkedIn, etc. were removed), so these are small self-contained SVGs
// with the same {size, className, style} API as a lucide icon component.
function FacebookIcon({ size = 24, className, style, ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} style={style} {...rest}>
      <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.5 1.49-3.89 3.77-3.89 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.44 2.91h-2.34v7.03C18.34 21.24 22 17.08 22 12.06Z" />
    </svg>
  );
}

function InstagramIcon({ size = 24, className, style, ...rest }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
      className={className} style={style} {...rest}
    >
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function LinkedinIcon({ size = 24, className, style, ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} style={style} {...rest}>
      <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm7 0h3.6v1.7h.05c.5-.95 1.75-1.95 3.6-1.95 3.85 0 4.55 2.5 4.55 5.75V21h-4v-5.5c0-1.3-.02-3-1.85-3-1.85 0-2.15 1.4-2.15 2.9V21h-4V9Z" />
    </svg>
  );
}

// Social icon buttons, each in its own brand color. Hover state is
// tracked in React (not a CSS class) because the icon's inline
// `style={{ color }}` would otherwise always beat a `group-hover`
// utility class on specificity.
function SocialIcons({ links }) {
  const [hovered, setHovered] = useState(null);

  return (
    <div className="flex items-center gap-2.5">
      {links.map(({ href, label, Icon, color, background, hoverBackground }) => {
        const isHovered = hovered === label;
        return (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-cursor="hover"
            aria-label={label}
            onMouseEnter={() => setHovered(label)}
            onMouseLeave={() => setHovered(null)}
            className="h-9 w-9 rounded-full flex items-center justify-center transition-all duration-200"
            style={{ background: isHovered ? hoverBackground || color : background }}
          >
            <Icon
              size={16}
              style={{ color: isHovered ? "#FFFFFF" : color }}
              className="transition-colors duration-200"
            />
          </a>
        );
      })}
    </div>
  );
}

// Same maroon/crimson tokens as the navbar — the footer is the other
// "dark bookend" of the page, with light content in between.
const theme = {
  bg: "#4E0F17",
  bgDeep: "#340A10",
  accent: "#E63946",
  text: "#FFF8F6",
  textMuted: "#C99298",
  border: "rgba(255,255,255,0.1)",
};

const services = [
  { to: "/webDevelopment", label: "Web Development" },
  { to: "/MobileApp", label: "Mobile App Development" },
  { to: "/UI", label: "UI / UX Design" },
  { to: "/EnterpriseSolutions", label: "Custom Software" },
];

const quickLinks = [
  { to: "/", label: "Home" },
  { to: "/Work", label: "Work" },
  { to: "/Services", label: "Services" },
  { to: "/Careers", label: "Careers" },
  { to: "/Contact", label: "Contact" },
];

// Social profiles shown in the "Follow Us" row — each uses its real
// brand color rather than the site's red accent.
const socialLinks = [
  {
    href: "https://www.facebook.com/UniBcomp/",
    label: "Facebook",
    Icon: FacebookIcon,
    color: "#1877F2",
    background: "rgba(24,119,242,0.14)",
  },
  {
    href: "https://www.instagram.com/unibcomp/",
    label: "Instagram",
    Icon: InstagramIcon,
    color: "#E1306C",
    background: "rgba(225,48,108,0.14)",
    hoverBackground: "linear-gradient(45deg,#FEDA75,#FA7E1E,#D62976,#962FBF,#4F5BD5)",
  },
  {
    href: "https://www.linkedin.com/company/unibcomp/",
    label: "LinkedIn",
    Icon: LinkedinIcon,
    color: "#0A66C2",
    background: "rgba(10,102,194,0.14)",
  },
];

// Legal links shown in the bottom bar, alongside Privacy Policy / Terms.
const legalLinks = [
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms of Service" },
  { to: "/refund-cancellation", label: "Refund & Cancellation" },
  { to: "/grievance-redressal", label: "Grievance & Redressal Policy" },
];

// Small reusable link row with an arrow that slides in on hover —
// gives the columns some life instead of static text stacks.
function FooterLink({ to, label }) {
  return (
    <Link
      to={to}
      data-cursor="hover"
      style={{ color: theme.textMuted }}
      className="group flex items-center gap-1.5 hover:text-white transition-colors w-fit"
    >
      <span>{label}</span>
      <ArrowRight
        size={13}
        className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200"
        style={{ color: theme.accent }}
      />
    </Link>
  );
}

export default function Footer() {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer className="relative pt-16 overflow-hidden" style={{ backgroundColor: theme.bg }}>
      {/* ================= NEWSLETTER — straddles the seam ================= */}
      <RevealSection className="relative z-20 max-w-6xl mx-auto px-6 -mt-24 mb-20" y={40}>
        <div
          className="rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl ring-1"
          style={{ backgroundColor: "#FFFFFF", boxShadow: "0 20px 60px -15px rgba(78,15,23,0.45)" }}
        >
          <div className="text-center md:text-left">
            <h4 className="text-2xl font-extrabold" style={{ color: "#221619" }}>
              Subscribe to our newsletter
            </h4>
            <p className="mt-1.5" style={{ color: "#6B6265" }}>
              Get product updates, tech insights and company news — no spam, ever.
            </p>
          </div>
          <form
            className="flex w-full md:w-auto shrink-0"
            onSubmit={(e) => e.preventDefault()}
          >
            <label htmlFor="footer-newsletter-email" className="sr-only">
              Email address
            </label>
            <input
              id="footer-newsletter-email"
              type="email"
              required
              placeholder="Enter your email"
              style={{ borderColor: "#E7E7E9" }}
              className="w-full md:w-72 px-5 py-3.5 rounded-l-full bg-white border border-r-0 text-[#221619] placeholder:text-[#9B9296] focus:outline-none focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/15 transition-all"
            />
            <MagneticButton strength={0.2} className="shrink-0">
              <button
                type="submit"
                data-cursor="hover"
                style={{ backgroundColor: theme.accent }}
                className="px-7 py-3.5 text-white font-semibold rounded-r-full hover:brightness-110 active:scale-[0.98] transition-all shrink-0"
              >
                Subscribe
              </button>
            </MagneticButton>
          </form>
        </div>
      </RevealSection>

      {/* ================= MAIN GRID ================= */}
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        className="relative z-10 max-w-7xl mx-auto px-6 grid gap-12 md:grid-cols-2 lg:grid-cols-5"
      >
        {/* Company Info */}
        <motion.div variants={staggerItem} className="lg:col-span-2">
          <img src={Logo} alt="UnibComp Logo" className="h-10 w-auto brightness-0 invert" />
          <p className="mt-6 leading-relaxed" style={{ color: theme.textMuted }}>
            UniBcomp is a modern software development company delivering
            scalable, secure, and high-performance digital solutions for
            startups, enterprises, and growing businesses.
          </p>
          <p className="mt-4 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.4)" }}>
            We specialize in building products that are fast, reliable, and
            designed to scale with your business.
          </p>

          {/* Small credibility strip — reinforces trust without icons/logos */}
          <div
            className="mt-8 flex items-center gap-6 pt-6 border-t"
            style={{ borderColor: theme.border }}
          >
            <div>
              <p className="text-xl font-extrabold" style={{ color: theme.text }}>150+</p>
              <p className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>Projects Delivered</p>
            </div>
            <div className="h-8 w-px" style={{ backgroundColor: theme.border }} />
            <div>
              <p className="text-xl font-extrabold" style={{ color: theme.text }}>5+</p>
              <p className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>Years of Experience</p>
            </div>
          </div>
        </motion.div>

        {/* Services */}
        <motion.div variants={staggerItem}>
          <h3
            className="font-semibold text-lg mb-5 pb-3 border-b"
            style={{ color: theme.text, borderColor: theme.border }}
          >
            Services
          </h3>
          <ul className="space-y-3.5">
            {services.map((link) => (
              <li key={link.to}>
                <FooterLink {...link} />
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Quick Links */}
        <motion.div variants={staggerItem}>
          <h3
            className="font-semibold text-lg mb-5 pb-3 border-b"
            style={{ color: theme.text, borderColor: theme.border }}
          >
            Quick Links
          </h3>
          <ul className="space-y-3.5">
            {quickLinks.map((link) => (
              <li key={link.to}>
                <FooterLink {...link} />
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Contact & Careers */}
        <motion.div variants={staggerItem}>
          <h3
            className="font-semibold text-lg mb-5 pb-3 border-b"
            style={{ color: theme.text, borderColor: theme.border }}
          >
            Get in Touch
          </h3>
          <ul className="space-y-4 text-sm">
            <li>
              <a
                href="https://maps.app.goo.gl/vX4JZ4pk4XnAW9VFA"
                target="_blank"
                rel="noopener noreferrer"
                data-cursor="hover"
                className="group flex items-start gap-3 hover:text-white transition-colors"
                style={{ color: theme.textMuted }}
              >
                <span
                  className="shrink-0 mt-0.5 h-8 w-8 rounded-full flex items-center justify-center transition-colors"
                  style={{ backgroundColor: "rgba(230,57,70,0.12)" }}
                >
                  <MapPin size={15} style={{ color: theme.accent }} />
                </span>
                <span className="leading-snug pt-1.5">
                  UniBcomp Networks Private Limited,
Unit No. 702, 7th Floor
Vipul Business Park,
Sohna Road, Sector 48,
Gurugram, Haryana-122018
                </span>
              </a>
            </li>
            <li className="pt-1">
              <span className="block text-xs font-semibold mb-2.5" style={{ color: "rgba(255,255,255,0.4)" }}>
                Follow Us
              </span>
              <SocialIcons links={socialLinks} />
            </li>
            <li>
              <a
                href="mailto:contact@unibcomp.co.in"
                data-cursor="hover"
                className="group flex items-center gap-3 hover:text-white transition-colors"
                style={{ color: theme.textMuted }}
              >
                <span
                  className="shrink-0 h-8 w-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "rgba(230,57,70,0.12)" }}
                >
                  <Mail size={15} style={{ color: theme.accent }} />
                </span>
                support@unibcomp.net
              </a>
            </li>
            <li>
              <a
                href="tel:+911242577777"
                data-cursor="hover"
                className="group flex items-center gap-3 hover:text-white transition-colors"
                style={{ color: theme.textMuted }}
              >
                <span
                  className="shrink-0 h-8 w-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "rgba(230,57,70,0.12)" }}
                >
                  <Phone size={15} style={{ color: theme.accent }} />
                </span>
                +91 1800 890 5311
              </a>
            </li>
            <li className="pt-2">
              <Link 
                to="/careers"
                data-cursor="hover"
                style={{ color: theme.accent }}
                className="inline-flex items-center gap-2 font-semibold hover:brightness-110 transition-all group"
              >
                View Open Positions
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </li>
          </ul>
        </motion.div>
      </motion.div>

      {/* ================= WATERMARK + BOTTOM BAR ================= */}
      <div className="relative mt-20 overflow-hidden" style={{ backgroundColor: theme.bgDeep }}>
        {/* giant ghost wordmark — same "signature" device as the ghost
           numerals on the services section, reused here as a watermark */}
        <style>{`
          @keyframes watermarkDrift {
            0%, 100% { transform: translateX(-50%) translateY(0); }
            50% { transform: translateX(-50%) translateY(-6px); }
          }
          .footer-watermark { animation: watermarkDrift 8s ease-in-out infinite; }
          @media (prefers-reduced-motion: reduce) { .footer-watermark { animation: none; } }
        `}</style>
        <span
          aria-hidden="true"
          className="footer-watermark pointer-events-none select-none absolute left-1/2 -bottom-10 md:-bottom-16 text-[8rem] sm:text-[11rem] md:text-[15rem] lg:text-[17rem] font-extrabold tracking-tight whitespace-nowrap"
          style={{ color: "rgba(255,255,255,0.05)" }}
        >
          UNIBCOMP
        </span>

        <div
          className="relative z-10 max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row justify-between items-center gap-4 text-sm border-t"
          style={{ borderColor: theme.border, color: "rgba(255,255,255,0.4)" }}
        >
          <p>
            © {new Date().getFullYear()} UniBcomp Networks Private Limited. All
            rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {legalLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-cursor="hover"
                className="hover:text-white transition-colors whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}
            <MagneticButton strength={0.4}>
              <button
                onClick={scrollToTop}
                data-cursor="hover"
                aria-label="Back to top"
                className="h-9 w-9 rounded-full flex items-center justify-center border transition-colors hover:text-white hover:border-white/30"
                style={{ borderColor: theme.border }}
              >
                <ArrowUp size={16} />
              </button>
            </MagneticButton>
          </div>
        </div>
      </div>
    </footer>
  );
}